import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { GET_URL_NEW } from '@utils/api/endpoints';
import { BaseError, BaseResponse } from '@utils/types/shared/base';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';

export interface CustomerBoActivity {
  id: number;
  session_id: string;
  session_mode: string;
  status: string;
  duration: number;
  language: string;
  filename: string;
  date_session_start: string;
  last_update: string;
  is_deleted: boolean;
}

export type CustomerBoActivityResponse = BaseResponse<CustomerBoActivity[]>;

export const useGetCustomerBoActivity = (customerId: number) => {
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const sortField = (searchParams.sort as string | undefined) || undefined;
  const sortDirection =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;

  const qString = (searchParams.q as string | undefined) || undefined;

  // Extract individual filter parameters
  const sessionIdFilter =
    (searchParams.session_id as string | undefined) || undefined;
  const filenameFilter =
    (searchParams.filename as string | undefined) || undefined;
  const statusFilter = (searchParams.status as string | undefined) || undefined;
  const languageFilter =
    (searchParams.language as string | undefined) || undefined;
  const sessionModeFilter =
    (searchParams.session_mode as string | undefined) || undefined;
  const isDeletedFilter =
    (searchParams.is_deleted as string | undefined) || undefined;

  const fetchActivity = async () => {
    const params = {
      offset,
      limit,
      q: qString,
      sort: sortField,
      direction: sortDirection,
      session_id: sessionIdFilter,
      filename: filenameFilter,
      status: statusFilter,
      language: languageFilter,
      session_mode: sessionModeFilter,
      is_deleted: isDeletedFilter
    };

    const { customerActivityByCustomerId } = GET_URL_NEW(params);
    const url = customerActivityByCustomerId(customerId);

    const { data } = await Axios.get<CustomerBoActivityResponse>(url);
    return data;
  };

  return useQuery<CustomerBoActivityResponse, AxiosError<BaseError>>(
    [
      'customerActivity',
      customerId,
      offset,
      limit,
      qString,
      sortField,
      sortDirection,
      sessionIdFilter,
      filenameFilter,
      statusFilter,
      languageFilter,
      sessionModeFilter,
      isDeletedFilter
    ],
    fetchActivity,
    {
      enabled: Number.isFinite(customerId) && customerId > 0,
      keepPreviousData: true,
      useErrorBoundary: false
    }
  );
};
